/**
 * Live-playback side of screen transitions: a temporary fixed canvas floated
 * over the editor composites the outgoing and incoming screen snapshots with
 * the same drawTransitionFrame the export path uses, then removes itself.
 * Purely presentational — scene, overrides and viewport are untouched until
 * the caller cuts the viewport after onDone.
 */
import { gsap } from "gsap";

import { drawTransitionFrame } from "./transitions";

import type { ScreenTransition } from "./transitions";

const PROGRESS_EASE = gsap.parseEase("power2.inOut");

/** Contain-fits a screen snapshot onto a stage of the overlay's size. */
const stageSnapshot = (
  snapshot: HTMLCanvasElement,
  width: number,
  height: number,
  background: string,
) => {
  const stage = document.createElement("canvas");
  stage.width = width;
  stage.height = height;
  const ctx = stage.getContext("2d")!;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);
  const fit = Math.min(
    width / Math.max(1, snapshot.width),
    height / Math.max(1, snapshot.height),
    1,
  );
  const w = snapshot.width * fit;
  const h = snapshot.height * fit;
  ctx.drawImage(snapshot, (width - w) / 2, (height - h) / 2, w, h);
  return stage;
};

/**
 * Runs one transition on an overlay canvas over the `.excalidraw` container.
 * Returns a cancel function; BOTH completion and cancel call `onDone` exactly
 * once (the sequence loop always resumes).
 */
export const playTransitionOverlay = ({
  from,
  to,
  transition,
  background,
  onDone,
  draw,
}: {
  from?: HTMLCanvasElement;
  to?: HTMLCanvasElement;
  transition: ScreenTransition;
  background: string;
  onDone: () => void;
  /** custom frame painter (smart animate) — supersedes from/to compositing */
  draw?: (
    ctx: CanvasRenderingContext2D,
    progress: number,
    width: number,
    height: number,
  ) => void;
}): (() => void) => {
  const host = document.querySelector(".excalidraw");
  const rect = host?.getBoundingClientRect() ?? {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(2, Math.round(rect.width * dpr));
  const height = Math.max(2, Math.round(rect.height * dpr));

  const overlay = document.createElement("canvas");
  overlay.className = "screen-transition-overlay";
  overlay.width = width;
  overlay.height = height;
  Object.assign(overlay.style, {
    position: "fixed",
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    pointerEvents: "none",
    zIndex: "30",
  });
  document.body.appendChild(overlay);
  const ctx = overlay.getContext("2d")!;

  const stagedFrom = draw
    ? null
    : stageSnapshot(from!, width, height, background);
  const stagedTo = draw ? null : stageSnapshot(to!, width, height, background);

  let raf = 0;
  let finished = false;
  const finish = () => {
    if (finished) {
      return;
    }
    finished = true;
    cancelAnimationFrame(raf);
    overlay.remove();
    onDone();
  };

  const start = performance.now();
  const step = (now: number) => {
    const p = Math.min(1, (now - start) / transition.durationMs);
    if (draw) {
      draw(ctx, PROGRESS_EASE(p), width, height);
    } else {
      drawTransitionFrame(ctx, stagedFrom!, stagedTo!, {
        type: transition.type,
        direction: transition.direction,
        progress: PROGRESS_EASE(p),
        width,
        height,
      });
    }
    if (p >= 1) {
      finish();
      return;
    }
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);

  return finish;
};
